import { z } from "zod";

/**
 * Safe user schema for createdByUser field in file responses.
 * The API currently leaks sensitive fields (password, accessToken, etc.).
 * This schema defines only the fields the UI needs; extra fields are
 * stripped by Zod's default parse mode so they don't propagate.
 *
 * WARNING: If this schema triggers validation failures, it means the API
 * response shape changed — not that sensitive fields were removed.
 * The real fix is on the backend (exclude sensitive fields from the query).
 */
const createdByUserSchema = z
	.object({
		id: z.string(),
		firstName: z.string(),
		lastName: z.string(),
		email: z.string(),
		imageLink: z.string().nullable(),
	})
	.nullable();

/**
 * Fields that should NEVER appear in API responses.
 * Used by validateResponse to log security warnings.
 */
export const SENSITIVE_USER_FIELDS = [
	"password",
	"accessToken",
	"setPasswordToken",
	"verifyToken",
	"twoFAcode",
];

const buildingDisciplineFileSchema = z.object({
	id: z.string(),
	deliveredFromJobId: z.string().nullable(),
	isDelivered: z.boolean().nullable(),
	isPreview: z.boolean().nullable(),
	previewLink: z.string().nullable(),
	viewer: z.string().nullable(),
	uuid: z.string().nullable(),
	buildingDiscipline: z
		.object({
			id: z.number(),
			building: z
				.object({
					id: z.string(),
				})
				.passthrough(),
			discipline: z.object({
				id: z.number(),
				type: z.string(),
				deletedAt: z.string().nullable(),
				sortOrder: z.number(),
			}),
		})
		.optional(),
	deliveredFromJob: z.any().nullable(),
});

const fileListItemSchema = z.object({
	id: z.string(),
	path: z.string(),
	key: z.string(),
	name: z.string(),
	size: z.string().nullable(),
	extension: z.string(),
	createdAt: z.string().nullable(),
	createdAtTZ: z.string().nullable(),
	version: z.number().nullable(),
	deletedAt: z.string().nullable(),
	archivedAt: z.string().nullable(),
	isNormalised: z.boolean().nullable(),
	isSliced: z.boolean().nullable(),
	publicUrl: z.string().nullable(),
	forgeUrnPreviewLink: z.string().nullable(),
	localDerivativePath: z.string().nullable(),
	derivativeStorageStatus: z.string().nullable(),
	derivativeTransferredAt: z.string().nullable(),
	order: z.any().nullable(),
	jobFiles: z.array(z.any()),
	jobStatusFiles: z.array(z.any()),
	createdByUser: createdByUserSchema,
	buildingDisciplineFile: z.array(buildingDisciplineFileSchema),
});

export const fileListResponseSchema = z.object({
	status: z.boolean(),
	meta: z.object({}).passthrough(),
	result: z.array(fileListItemSchema),
});

export {
	fileListItemSchema,
	buildingDisciplineFileSchema,
	createdByUserSchema,
};
